import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';

interface OrgMember {
  id: string;
  name: string;
  title: string;
  department: string | null;
  photo: string | null;
  bio: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  twitterUrl: string | null;
  isFeatured: boolean;
  featuredQuote: string | null;
  parentId: string | null;
  displayOrder: number;
}

/**
 * 🏛️ صفحة "عن شركة صورة العقارية"
 *
 * تحتوي على:
 *   1) Hero هادئ بنفس هوية الموقع
 *   2) كلمة الرئيس التنفيذي (مع صورة + توقيع)
 *   3) كلمة عن العقار + رؤية 2030 + دور سمو ولي العهد
 *   4) القيم والرؤية والرسالة
 *   5) الهيكل التنظيمي (مخطط هرمي)
 *   6) CTA
 */
export function AboutPage() {
  const { data } = useQuery({
    queryKey: ['org', 'members'],
    queryFn: () => api.get<ApiSuccess<OrgMember[]>>('/api/org/members'),
  });
  // ⭐ نقرأ "العضو المميّز" بشكل منفصل لأن endpoint /featured يُنشئ الرئيس التنفيذي تلقائياً
  //   لو ما كان موجود في DB (auto-seed). هذا يضمن دائماً ظهور كلمة الرئيس.
  const { data: featuredRes } = useQuery({
    queryKey: ['org', 'featured'],
    queryFn: () => api.get<ApiSuccess<OrgMember | null>>('/api/org/featured'),
  });
  const members = data?.data ?? [];
  const featured = featuredRes?.data ?? members.find((m) => m.isFeatured) ?? null;
  const others = members.filter((m) => !m.isFeatured);

  // بناء هرمية الأعضاء (parent → children)
  const roots = others.filter((m) => !m.parentId);
  const childrenOf = (id: string) => others.filter((m) => m.parentId === id);

  return (
    <div>
      {/* ━━━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="relative overflow-hidden text-ink"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 15% 0%, rgba(201,162,79,0.08) 0%, transparent 55%),' +
            'radial-gradient(ellipse 70% 60% at 85% 100%, rgba(107,141,177,0.20) 0%, transparent 55%),' +
            'linear-gradient(135deg, #dde8f5 0%, #e9f0f9 35%, #eef4fb 65%, #dce6f3 100%)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24 text-center relative">
          <span className="inline-flex items-center gap-2 bg-navy-soft text-navy-dark text-xs font-bold px-3 py-1.5 rounded-full mb-4 border border-navy-light/20">
            من نحن
          </span>
          <h1 className="text-4xl md:text-6xl font-black leading-tight mb-3 text-navy-darker">
            عن <span className="text-gold-dark">شركة صورة</span> العقارية
          </h1>
          <p className="text-ink-secondary text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-8">
            رواد العقار في المملكة العربية السعودية — نقدّم تجربة استثنائية في البيع والشراء
            والمزادات وإدارة العقارات وفق أعلى معايير الشفافية والاحترافية.
          </p>

          {/* 🏆 شريط الإنجازات — يبدأ بسنة التأسيس 2016 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
            <HeroStat number="2016" label="عام التأسيس" sub="منذ" />
            <HeroStat number={`${new Date().getFullYear() - 2016}+`} label="سنة من العطاء" sub="خبرة" />
            <HeroStat number="REGA" label="معتمدون" sub="رسميًا" />
            <HeroStat number="13" label="منطقة" sub="نخدم" />
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ رحلتنا منذ 2016 — Timeline أفقي (مرفوع لفوق) ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-12 bg-gradient-to-b from-white to-navy-soft/20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-2">
              رحلتنا
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-navy-darker">
              منذ <span className="text-gold-dark">2016</span> ونحن نكتب القصة
            </h2>
            <p className="text-ink-secondary mt-1 text-sm">من فكرة بسيطة إلى منصة سعودية متكاملة</p>
          </div>

          {(() => {
            const milestones = [
              { year: '2016', title: 'نقطة الانطلاق', body: 'تأسست شركة صورة العقارية على أيدي رجال أعمال سعوديين بإيمانٍ راسخٍ بمستقبل العقار في المملكة.' },
              { year: '2018', title: 'التوسع الميداني', body: 'بناء قاعدة عملاء واسعة وتغطية أبرز المدن السعودية بخدمات عقارية احترافية متكاملة.' },
              { year: '2020', title: 'التحوّل الرقمي', body: 'انطلاق مرحلة الرقمنة — تطوير أدوات داخلية للإدارة العقارية وتقديم خدمات رقمية للعملاء.' },
              { year: '2022', title: 'الاعتماد النظامي', body: 'استكمال جميع متطلبات الهيئة العامة للعقار (REGA) والتسجيل في منصات إيجار وفال وناجز.' },
              { year: '2026', title: 'منصة صورة الجديدة', body: 'منصة رقمية متكاملة بمعايير عالمية — مزادات حيّة، اتحاد ملاك، تقنيات تواكب رؤية 2030.', latest: true },
            ];
            return (
              <div className="relative">
                {/* أفقي على الديسكتوب */}
                <div className="hidden md:flex items-stretch justify-center gap-0 px-2">
                  {milestones.map((m, i) => (
                    <div key={m.year} className="flex items-stretch shrink-0">
                      <TimelineCard {...m} />
                      {i < milestones.length - 1 && <TimelineArrow />}
                    </div>
                  ))}
                </div>

                {/* رأسي على الجوال */}
                <div className="md:hidden space-y-3">
                  {milestones.map((m, i) => (
                    <div key={m.year}>
                      <TimelineCard {...m} mobile />
                      {i < milestones.length - 1 && (
                        <div className="flex justify-center py-1">
                          <span className="text-gold text-2xl">↓</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ كلمة الرئيس التنفيذي ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-2">
              كلمة الرئيس التنفيذي
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-navy-darker">رؤيتنا للعقار في المملكة</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 items-start">
            {/* صورة وبيانات الرئيس */}
            <div className="text-center md:text-start">
              <div className="relative inline-block">
                <div className="w-56 h-56 mx-auto md:mx-0 rounded-2xl overflow-hidden ring-4 ring-gold/20 shadow-xl bg-gradient-to-br from-navy-soft to-white">
                  <img
                    src={featured?.photo || '/team/ceo-ali-alhowaimel.jpg'}
                    alt={featured?.name ?? 'علي بن فهد الهويمل'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // 🛡️ fallback لو الصورة ما تحمّلت
                      e.currentTarget.style.display = 'none';
                      const sibling = e.currentTarget.nextElementSibling as HTMLElement | null;
                      if (sibling) sibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full items-center justify-center text-7xl text-navy-dark/30 hidden">
                    👤
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 w-14 h-14 rounded-2xl bg-gold flex items-center justify-center text-white text-2xl shadow-lg">
                  ✒️
                </div>
              </div>
              <div className="mt-5">
                <div className="font-black text-navy-darker text-lg">
                  {featured?.name ?? 'علي بن فهد الهويمل'}
                </div>
                <div className="text-gold-dark text-sm font-bold mt-0.5">
                  {featured?.title ?? 'الرئيس التنفيذي - شريك مؤسس'}
                </div>
                {featured?.bio && (
                  <p className="text-ink-muted text-xs mt-2 leading-relaxed">{featured.bio}</p>
                )}
                {(featured?.linkedinUrl || featured?.twitterUrl) && (
                  <div className="flex gap-2 justify-center md:justify-start mt-3">
                    {featured.linkedinUrl && (
                      <a href={featured.linkedinUrl} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-navy-soft text-navy-dark hover:bg-navy hover:text-white flex items-center justify-center transition text-xs font-bold">in</a>
                    )}
                    {featured.twitterUrl && (
                      <a href={featured.twitterUrl} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-navy-soft text-navy-dark hover:bg-navy hover:text-white flex items-center justify-center transition text-xs">𝕏</a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* الكلمة */}
            <div className="bg-gradient-to-br from-navy-soft/40 to-gold-soft/30 rounded-3xl p-6 md:p-10 border border-navy-soft relative">
              {/* علامات اقتباس فخمة */}
              <div className="absolute top-4 right-4 text-6xl text-gold/20 font-serif leading-none">"</div>
              <div className="absolute bottom-4 left-4 text-6xl text-gold/20 font-serif leading-none rotate-180">"</div>

              <div className="relative space-y-4 text-ink-secondary leading-loose text-sm md:text-base">
                {featured?.featuredQuote ? (
                  <SpeechBody quote={featured.featuredQuote} />
                ) : (
                  // الكلمة الافتراضية الفخمة (تظهر إن لم يكن هناك كلمة مخصّصة)
                  <DefaultCEOQuote />
                )}

                <div className="pt-4 mt-4 border-t border-gold/20 text-end">
                  <div className="text-gold-dark font-black text-lg">
                    {featured?.name ?? 'علي بن فهد الهويمل'}
                  </div>
                  <div className="text-ink-muted text-xs mt-0.5">
                    {featured?.title ?? 'الرئيس التنفيذي - شريك مؤسس'}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ رؤية 2030 + دور القيادة ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div
            className="rounded-3xl p-8 md:p-12 text-white shadow-lg relative overflow-hidden"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <div className="text-center mb-8">
              <span className="inline-block bg-white/10 backdrop-blur border border-white/15 text-white/90 text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                رؤية المملكة 2030
              </span>
              <h2 className="text-2xl md:text-4xl font-black mb-3 leading-tight">
                العقار في المملكة العربية السعودية
              </h2>
              <p className="text-white/70 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
                تشهد المملكة العربية السعودية نهضة عقارية غير مسبوقة بقيادة سمو ولي العهد رئيس مجلس الوزراء
                <strong className="text-gold-light"> الأمير محمد بن سلمان بن عبدالعزيز آل سعود </strong>
                — رؤية تستهدف تحويل القطاع العقاري إلى ركيزة من ركائز الاقتصاد الوطني وفق رؤية المملكة 2030.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <VisionCard
                num="70%"
                title="نسبة تملّك المساكن"
                body="مستهدف رؤية 2030 لرفع نسبة تملّك المواطنين للمساكن — تشريعات، تمويل، ومشاريع إسكان عملاقة"
              />
              <VisionCard
                num="+1.3T"
                title="ريال — حجم القطاع"
                body="القطاع العقاري السعودي يُعد من أكبر القطاعات المساهمة في الناتج المحلي بقيادة المشاريع الكبرى"
              />
              <VisionCard
                num="100%"
                title="رقمنة القطاع"
                body="منظومة كاملة (REGA, إيجار, ناجز, فال) تربط الجميع رقمياً وتحفظ الحقوق"
              />
            </div>

            <div className="mt-8 bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
              <h3 className="font-black text-gold-light text-lg mb-3">دور القيادة الرشيدة</h3>
              <p className="text-white/80 text-sm leading-loose">
                تحت قيادة خادم الحرمين الشريفين الملك سلمان بن عبدالعزيز آل سعود وسمو ولي العهد الأمير محمد بن سلمان،
                أطلقت المملكة منظومة من المشاريع العملاقة (نيوم، البحر الأحمر، القدية، روشن، ذا لاين) التي ترسم
                ملامح مستقبل عقاري عالمي. وأنشئت <strong className="text-gold-light">الهيئة العامة للعقار (REGA)</strong> لتنظيم القطاع،
                وأطلقت منصات <strong className="text-gold-light">إيجار</strong> لتوثيق العقود و
                <strong className="text-gold-light"> فال</strong> لاعتماد الوسطاء، و
                <strong className="text-gold-light"> ناجز</strong> للخدمات العدلية —
                وفّرت بذلك بيئة شفّافة وآمنة لكل أطراف العملية العقارية.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ رؤيتنا – رسالتنا – قيمنا ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ValueCard
              icon="🎯"
              title="رؤيتنا"
              body="أن نكون المنصة العقارية الأولى في المملكة بتقنية متقدمة وثقة راسخة وتجربة عميل استثنائية."
            />
            <ValueCard
              icon="🤝"
              title="رسالتنا"
              body="ربط المُلّاك والمستثمرين والمشترين في بيئة شفّافة وآمنة، مع خدمات عقارية متكاملة وفق أنظمة المملكة."
            />
            <ValueCard
              icon="⭐"
              title="قيمنا"
              body="الشفافية المطلقة، الالتزام بالأنظمة، الابتكار الدائم، والاحترام لكل عميل في كل خطوة."
            />
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ الهيكل التنظيمي ━━━━━━━━━━━━━━━━━━━━ */}
      {others.length > 0 && (
        <section className="px-4 py-16 bg-gradient-to-b from-white to-navy-soft/20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-2">
                الفريق
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-navy-darker">الهيكل التنظيمي</h2>
              <p className="text-ink-secondary mt-2">القيادات والإدارات التي تقود مسيرتنا</p>
            </div>

            {/* عرض هرمي بسيط */}
            <div className="space-y-8">
              {roots.map((root) => (
                <OrgBranch key={root.id} member={root} childrenOf={childrenOf} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="rounded-3xl p-8 md:p-12 text-white shadow-lg relative overflow-hidden"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-black mb-2 leading-tight">
                  ابدأ رحلتك العقارية مع شركة صورة
                </h2>
                <p className="text-white/75 text-sm md:text-base">
                  ثقتك تستحق منصة معتمدة، شفّافة، ومتوافقة مع أنظمة المملكة وتوجّهات رؤية 2030.
                </p>
              </div>
              <div className="flex gap-3 flex-wrap md:justify-end">
                <Link to="/properties"><Button variant="gold" size="lg">تصفّح العقارات</Button></Link>
                <Link to="/contact">
                  <Button variant="outline" size="lg" className="!bg-white !text-navy-dark !border-white">
                    تواصل معنا
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━ Sub Components ━━━━━━━━━━━━━━━━━━━━ */

function DefaultCEOQuote() {
  return (
    <>
      <p className="text-justify">
        تشهد المملكة العربية السعودية مرحلة تحوّل تاريخية بقيادة سمو ولي العهد
        <strong className="text-navy-darker"> الأمير محمد بن سلمان بن عبدالعزيز آل سعود</strong>،
        ووفق <strong className="text-gold-dark">رؤية المملكة 2030</strong> التي وضعت القطاع العقاري
        في صدارة محرّكات الاقتصاد الوطني — قطاع يُسهم في رفاه المواطن، ويستقطب الاستثمار،
        ويُجسّد جودة الحياة على أرض الواقع.
      </p>
      <p className="text-justify">
        وفي ظل <strong className="text-gold-dark">التحوّل الرقمي الشامل</strong> الذي تقوده المملكة، أصبح
        مستقبل العقار رقمياً بكل تفاصيله — من البحث والمعاينة إلى التعاقد والمدفوعات والتوثيق. وقد
        جعلنا التقنية حجر الأساس في كل ما نقدّمه: <strong className="text-navy-darker">مزادات حيّة بالبثّ المرئي</strong>،
        <strong className="text-navy-darker"> توقيع إلكتروني معتمد</strong>، تكامل مباشر مع منصّات
        <strong className="text-navy-darker"> إيجار وفال وناجز</strong>، ومحفظة رقمية تُسهّل التعاملات بأمانٍ
        كامل وسرعةٍ تليق بإنسان هذا العصر.
      </p>
      <p className="text-justify">
        منذ تأسيسنا <strong className="text-gold-dark">عام 2016</strong>، حملنا أمانةً واحدة: أن نكون
        شريكاً موثوقاً للمُلّاك والمستثمرين على أرض المملكة. وعلى مدى هذه السنوات، رافقنا التحوّل
        الذي عاشه القطاع العقاري ونمونا معه — من السوق التقليدي إلى السوق الرقمي المنظَّم — حاملين
        معنا كل خبرة ميدانية اكتسبناها لنبنِيَ منها منصّة سعودية بمعايير عالمية.
      </p>
      <p className="text-justify">
        ومن هذا الإرث، أسّسنا <strong className="text-navy-darker">شركة صورة العقارية</strong> لتكون
        امتداداً طبيعياً لهذه النهضة — منصةً سعوديةً متكاملةً تخدم المُلّاك والمستثمرين والمشترين بأعلى
        معايير الاحترافية، وتلتزم التزاماً كاملاً بأنظمة <strong className="text-navy-darker">الهيئة العامة للعقار (REGA)</strong>،
        ومنظومة <strong className="text-navy-darker">إيجار</strong> و<strong className="text-navy-darker">فال</strong>،
        ووزارة العدل عبر <strong className="text-navy-darker">ناجز</strong>.
      </p>
      <p className="text-justify">
        نؤمن أن العقار ليس مجرد صفقة، بل ثقة وأمانة ومستقبل. ولذلك بنينا منصةً تُجمع فيها التقنية
        المتقدمة مع الخبرة الميدانية: مزادات حية موثّقة، إدارة احترافية لاتحادات الملاك، خدمات تطوير
        وتقييم وتأجير، وكل ذلك في بيئة شفافة تحفظ الحقوق وتُوصل التواصل المباشر بين كل الأطراف.
      </p>
      <p className="text-justify">
        ومن إيماننا الراسخ بأن <strong className="text-gold-dark">الجار حقّ</strong> وأن
        <strong className="text-gold-dark"> الأجزاء المشتركة أمانةٌ</strong> في أعناق الجميع، أطلقنا أول
        منظومة سعودية متكاملة <strong className="text-navy-darker">لاتحاد الملاك</strong> — تحفظ حقّ كل
        مالك في وحدته وفي الأجزاء المشتركة من المبنى، وتجعل قرارات الجمعية العمومية شفّافةً وموثّقة،
        وتُدير الرسوم والصيانة وحسابات الاحتياطي وفق أعلى معايير الحوكمة. فحين يُدار العقار بالعدل،
        يدوم بالخير، وتطمئن كل أسرةٍ على بيتها وعلى مستقبل أبنائها.
      </p>
      <p className="text-justify">
        نَعِد كل عميل وكل شريك بأن نظلّ أوفياء لقيمنا: <strong className="text-navy-darker">الشفافية، الالتزام، الابتكار،
        وراحة العميل</strong> — ونُدركُ أن مسؤوليتنا تجاه هذا الوطن المُعطاء مسؤولية كبيرة، نحملها
        بفخر وعناية، ونُجدّد البيعة والولاء لمقام خادم الحرمين الشريفين وسموّ ولي عهده.
      </p>
      <p className="text-justify">
        ندعو كل من يبحث عن تجربة عقارية تَستندُ إلى الأنظمة والمنصّات الرسمية أن يكون جزءاً من
        مسيرتنا. في كنف وطنٍ معطاء، وتحت مظلّة <strong className="text-navy-darker">منظومةٍ تشريعية رائدة</strong>،
        نسيرُ مع المملكة في تحوّلها الرقمي بكل فخر — ففي صورة العقارية، نحن لا نُسوّق العقار فحسب،
        بل نُقدّم{' '}
        <strong className="font-black text-navy-darker text-base md:text-lg">
          العقار بصورة جديدة
        </strong>
        .
      </p>
    </>
  );
}

/**
 * 📜 عرض كلمة الرئيس التنفيذي — جملة الختام تُعرض ضمن الكلمة بنفس التدفّق
 *    مع تمييز عبارة "العقار بصورة جديدة" بحجم وذهبي ضمن النص.
 */
function SpeechBody({ quote }: { quote: string }) {
  const paragraphs = quote.split('\n\n').filter(Boolean);
  const lastIdx = paragraphs.length - 1;
  return (
    <>
      {paragraphs.map((para, i) => {
        const isClosing = i === lastIdx && para.includes('العقار بصورة جديدة');
        if (!isClosing) return <p key={i} className="text-justify">{para}</p>;

        // تمييز عبارة "العقار بصورة جديدة" داخل الفقرة الختامية — بنفس لون النص لكن أبرز قليلاً
        const SLOGAN = 'العقار بصورة جديدة';
        const idx = para.indexOf(SLOGAN);
        const before = para.slice(0, idx);
        const after = para.slice(idx + SLOGAN.length);
        return (
          <p key={i} className="text-justify">
            {before}
            <strong className="font-black text-navy-darker text-base md:text-lg">
              {SLOGAN}
            </strong>
            {after}
          </p>
        );
      })}
    </>
  );
}

/**
 * 🏛️ بطاقة في الخط الزمني (Timeline) — أفقية وقابلة للوضع الرأسي على الجوال
 */
function TimelineCard({ year, title, body, latest, mobile }: {
  year: string; title: string; body: string; latest?: boolean; mobile?: boolean;
}) {
  const widthClass = mobile ? 'w-full' : 'w-[200px] md:w-[210px] lg:w-[230px]';
  return (
    <div
      className={`relative bg-white border-2 ${latest ? 'border-gold' : 'border-gray-100'} rounded-2xl p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition ${widthClass} flex flex-col`}
      dir="rtl"
    >
      {/* السنة + شارة "الحاضر" */}
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-xl md:text-2xl font-black tabular-nums ${latest ? 'text-gold-dark' : 'text-navy-dark'}`}>
          {year}
        </span>
        {latest && (
          <>
            <span className="text-[10px] bg-gold text-white font-black px-2 py-0.5 rounded-full whitespace-nowrap">الحاضر</span>
            <span className="relative flex h-2 w-2 ms-auto">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
            </span>
          </>
        )}
      </div>
      <h3 className="font-black text-navy-darker text-sm md:text-base mb-1.5 leading-tight">{title}</h3>
      <p className="text-[11px] md:text-xs text-ink-muted leading-relaxed">{body}</p>
    </div>
  );
}

/**
 * ➤ سهم بين بطاقات الخط الزمني (يظهر على الديسكتوب فقط)
 */
function TimelineArrow() {
  return (
    <div className="flex items-center justify-center px-1 md:px-2 self-center" aria-hidden="true">
      <svg width="34" height="20" viewBox="0 0 34 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gold">
        {/* الخط — في RTL السهم يتجه يساراً (من السنة الأقدم على اليمين إلى الأحدث على اليسار) */}
        <path
          d="M32 10 L4 10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="3 3"
          opacity="0.5"
        />
        {/* رأس السهم */}
        <path
          d="M10 4 L4 10 L10 16"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

/* 🏆 إحصاء صغير في الـ hero */
function HeroStat({ number, label, sub }: { number: string; label: string; sub: string }) {
  return (
    <div className="bg-white/70 backdrop-blur border border-navy-light/15 rounded-2xl px-3 py-3 shadow-sm hover:shadow-md transition">
      <div className="text-[10px] text-ink-muted uppercase tracking-wider">{sub}</div>
      <div className="text-2xl md:text-3xl font-black text-gold-dark tabular-nums leading-tight">{number}</div>
      <div className="text-[11px] text-navy-dark font-bold mt-0.5">{label}</div>
    </div>
  );
}

function VisionCard({ num, title, body }: { num: string; title: string; body: string }) {
  return (
    <div className="bg-white/8 backdrop-blur border border-white/10 rounded-2xl p-5 hover:bg-white/12 transition">
      <div className="text-3xl md:text-4xl font-black text-gold-light tabular-nums">{num}</div>
      <div className="font-black text-base mt-1.5">{title}</div>
      <div className="text-white/65 text-xs mt-1.5 leading-relaxed">{body}</div>
    </div>
  );
}

function ValueCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 hover:border-gold/40 hover:shadow-brand transition group">
      <div className="w-12 h-12 rounded-xl bg-navy-soft text-navy-dark flex items-center justify-center text-2xl mb-3 group-hover:scale-110 transition">
        {icon}
      </div>
      <h3 className="font-black text-base text-navy-darker mb-1.5">{title}</h3>
      <p className="text-xs text-ink-muted leading-relaxed">{body}</p>
    </div>
  );
}

function OrgBranch({ member, childrenOf }: { member: OrgMember; childrenOf: (id: string) => OrgMember[] }) {
  const children = childrenOf(member.id);
  return (
    <div className="text-center">
      <OrgCard member={member} large />
      {children.length > 0 && (
        <>
          {/* خط رابط بسيط */}
          <div className="w-px h-6 bg-gradient-to-b from-gold/40 to-transparent mx-auto" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {children.map((c) => (
              <div key={c.id}>
                <OrgCard member={c} />
                {/* أبناء المستوى الثاني */}
                {childrenOf(c.id).length > 0 && (
                  <div className="space-y-2 mt-3">
                    {childrenOf(c.id).map((g) => (
                      <OrgCard key={g.id} member={g} small />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function OrgCard({ member, large, small }: { member: OrgMember; large?: boolean; small?: boolean }) {
  const size = large ? 'p-6' : small ? 'p-3' : 'p-4';
  const photo = large ? 'w-20 h-20' : small ? 'w-10 h-10' : 'w-14 h-14';
  return (
    <div className={`bg-white border-2 ${large ? 'border-gold/40' : 'border-gray-100'} ${size} rounded-2xl shadow-sm hover:shadow-brand transition`}>
      <div className={`${photo} mx-auto rounded-2xl overflow-hidden ring-2 ring-gold/20 mb-2 bg-navy-soft`}>
        {(() => {
          // 🎯 fallback ذكي للرئيس التنفيذي يستخدم الصورة الستاتيكية
          const isCEO = member.name?.includes('علي بن فهد');
          const src = member.photo || (isCEO ? '/team/ceo-ali-alhowaimel.jpg' : null);
          return src ? (
            <img src={src} alt={member.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-navy-dark/40 text-xl">👤</div>
          );
        })()}
      </div>
      <div className={`font-black text-navy-darker ${large ? 'text-lg' : small ? 'text-xs' : 'text-sm'}`}>
        {member.name}
      </div>
      <div className={`text-gold-dark font-bold ${small ? 'text-[10px]' : 'text-xs'} mt-0.5`}>
        {member.title}
      </div>
      {member.department && !small && (
        <div className="text-[10px] text-ink-muted mt-0.5">{member.department}</div>
      )}
    </div>
  );
}
